import { Grid } from '@mui/material';
import { useFormik } from 'formik';
import * as yup from 'yup';
import type { Ciudad } from '../../stores/ciudades/ciudad';
import type { ActualizarCiudad, RegistrarCiudad } from '../../stores/ciudades/ciudades.store';
import { useUIStore } from '../../stores/ui/ui.store';
import type { CommonEntityFormProps } from '../../utils/common-entity-form-props';
import { getLengthOrErrorText } from '../../utils/get-length-or-error-text';
import AppTextField from '../ui/AppTextField';
import EditForm from '../ui/EditForm';

const validationSchema = yup.object({
  id: yup.number(),
  nombre: yup.string().required('El nombre es requerido')
});

type CiudadFormProps = {
  ciudad?: Ciudad;
  onSave:
    | ((values: ActualizarCiudad) => Promise<void>)
    | ((values: RegistrarCiudad) => Promise<number | null>);
  onClose?: () => void;
} & CommonEntityFormProps;

const CiudadForm = ({
  ciudad,
  esAgregar,
  onSave,
  onDelete,
  onActivate,
  onClose
}: CiudadFormProps) => {
  const esEditar = !esAgregar;
  const activo = esAgregar || ciudad?.activa === true;
  const inactivo = !activo;
  const status = activo ? 'ACTIVO' : 'INACTIVO';
  const { loading } = useUIStore();
  const initialValues = {
    id: ciudad?.id ?? '',
    nombre: ciudad?.nombre ?? ''
  };
  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: async (values) => {
      const newId = await onSave(values);
      if (newId) {
        formik.resetForm();
      }
    },
    enableReinitialize: true
  });

  const reset = () => formik.resetForm({ values: initialValues });

  let props = {
    module: 'ciudades',
    title: 'Ciudades',
    readonly: !activo,
    loading: loading,
    showActivate: false,
    showDelete: esEditar,
    onSave: formik.handleSubmit,
    onClear: reset,
    onDelete: onDelete,
    onActivate: onActivate
  };

  return (
    <EditForm {...(onClose ? { ...props, onClose } : { ...props, returnLink: '/ciudades' })}>
      <Grid>
        <Grid container spacing={2}>
          <Grid>
            <AppTextField
              name="id"
              label="Código"
              placeholder="Código"
              value={formik.values.id}
              readOnly={true}
            />
          </Grid>
          <Grid size="grow">
            <AppTextField
              name="nombre"
              label="Nombre"
              placeholder="Nombre"
              maxLength={40}
              disabled={loading || inactivo}
              value={formik.values.nombre}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              error={formik.touched.nombre && Boolean(formik.errors.nombre)}
              helperText={getLengthOrErrorText(
                formik.touched.nombre ? formik.errors.nombre : '',
                formik.values.nombre.length,
                40
              )}
            />
          </Grid>
        </Grid>
      </Grid>
      <Grid size={{ xs: 2 }}>
        <AppTextField label="Status" value={status} disabled={true} />
      </Grid>
    </EditForm>
  );
};

export default CiudadForm;
